import React, { useContext, useEffect, useMemo, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CardContent,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useLocation, useParams } from "react-router-dom";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import {
  CreateOrUpdateResponseUser,
  CreateOrUpdateResponseUser_Action,
  CreateOrUpdateResponseUser_ErrorDetails_Level,
  CreateOrUpdateResponseUser_ErrorDetails_Reason,
  CreateOrUpdateResponseUser_Field,
  CreateOrUpdateResponseUser_Status,
  CreateOrUpdateUserOrigin_Source,
  CreateOrUpdateUsersJob,
} from "@safetyculture/s12-apis-connect-web/s12/users/v1/create_or_update_pb";

import { adminGetCreateOrUpdateUsersJob } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import {
  countByUserStatus,
  formatActivity,
  formatOrigin,
  formatRelativeTimestamp,
  getPerformedBy,
  isIntegrationSource,
} from "components/org-user-jobs/helpers";
import { fetchUserNameMap } from "components/org-user-jobs/user-lookup";
import { DocPrefix } from "constants/index";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { toS12ID, toUUID } from "helpers/s12-helpers";

interface UrlParams {
  regionCode: string;
  roleId: string;
  jobId: string;
}

const USER_PAGE_LIMIT = 100;

type ActivityFilter = "create" | "update" | "no_change";
type ResultFilter = "success" | "completed_with_errors" | "errored";

const ACTIVITY_OPTIONS: Array<{ key: ActivityFilter; label: string }> = [
  { key: "create", label: "New user added" },
  { key: "update", label: "User updated" },
  { key: "no_change", label: "No change" },
];

const RESULT_OPTIONS: Array<{ key: ResultFilter; label: string }> = [
  { key: "success", label: "Success" },
  { key: "completed_with_errors", label: "Completed with errors" },
  { key: "errored", label: "Errored" },
];

const matchesActivity = (
  user: CreateOrUpdateResponseUser,
  active: Set<ActivityFilter>
): boolean => {
  if (active.size === 0) {
    return true;
  }
  const action =
    user.operation?.action ?? CreateOrUpdateResponseUser_Action.UNSPECIFIED;
  if (
    active.has("create") &&
    action === CreateOrUpdateResponseUser_Action.CREATE
  ) {
    return true;
  }
  if (
    active.has("update") &&
    action === CreateOrUpdateResponseUser_Action.UPDATE
  ) {
    return true;
  }
  if (
    active.has("no_change") &&
    action === CreateOrUpdateResponseUser_Action.NO_CHANGE
  ) {
    return true;
  }
  return false;
};

const matchesResult = (
  user: CreateOrUpdateResponseUser,
  active: Set<ResultFilter>
): boolean => {
  if (active.size === 0) {
    return true;
  }
  const status =
    user.operation?.status ?? CreateOrUpdateResponseUser_Status.UNSPECIFIED;
  if (
    active.has("errored") &&
    status === CreateOrUpdateResponseUser_Status.ERRORED
  ) {
    return true;
  }
  if (
    active.has("completed_with_errors") &&
    (status === CreateOrUpdateResponseUser_Status.COMPLETED_WITH_ERRORS ||
      status === CreateOrUpdateResponseUser_Status.VALIDATED_WITH_ERRORS)
  ) {
    return true;
  }
  if (
    active.has("success") &&
    (status === CreateOrUpdateResponseUser_Status.COMPLETED ||
      status === CreateOrUpdateResponseUser_Status.VALIDATED)
  ) {
    return true;
  }
  return false;
};

const enumLabel = (
  enumObj: Record<number, string>,
  value: number,
  prefix: string
): string => (enumObj[value] ?? "UNKNOWN").replace(prefix, "");

interface UserRowProps {
  user: CreateOrUpdateResponseUser;
  rowKey: string;
}

const formatErrorsFound = (count: number): string =>
  count === 1 ? "1 error found" : `${count} errors found`;

const UserRow = ({ user, rowKey }: UserRowProps) => {
  const [open, setOpen] = useState(false);

  const username = user.user?.username || user.user?.id || "—";
  const fields =
    user.operation?.fields.map((f) =>
      enumLabel(CreateOrUpdateResponseUser_Field, f, "FIELD_")
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    ) ?? [];

  const errorCount = user.errors.length;
  const hasErrors = errorCount > 0;
  const hasDetail = fields.length > 0 || hasErrors;

  return (
    <>
      <TableRow data-testid={`user-row-${rowKey}`}>
        <TableCell sx={{ wordBreak: "break-all" }}>{username}</TableCell>
        <TableCell>{formatActivity(user.operation?.action)}</TableCell>
        <TableCell align="right">
          <Stack direction="column" alignItems="flex-end" spacing={0.5}>
            <Typography
              variant="caption"
              color={hasErrors ? "error" : "textSecondary"}
              data-testid={`user-row-errors-${rowKey}`}
            >
              {formatErrorsFound(errorCount)}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell padding="checkbox" align="right">
          {hasDetail && (
            <IconButton
              size="small"
              aria-label="expand user row"
              onClick={() => setOpen(!open)}
              data-testid={`user-row-expand-${rowKey}`}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      {hasDetail && (
        <TableRow>
          <TableCell
            colSpan={4}
            sx={{ paddingBottom: 0, paddingTop: 0, borderBottom: open ? 1 : 0 }}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ paddingY: 1.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 0.5 }}
                  data-testid={`user-row-info-heading-${rowKey}`}
                >
                  Info
                </Typography>
                {fields.length > 0 ? (
                  <Typography variant="body2" component="div">
                    Fields changed: {fields.join(", ")}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No field changes recorded.
                  </Typography>
                )}

                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}
                  data-testid={`user-row-errors-heading-${rowKey}`}
                >
                  Errors
                </Typography>
                {hasErrors ? (
                  user.errors.map((err, errIndex) => {
                    const levelName = enumLabel(
                      CreateOrUpdateResponseUser_ErrorDetails_Level,
                      err.level,
                      "LEVEL_"
                    );
                    const reasonName = enumLabel(
                      CreateOrUpdateResponseUser_ErrorDetails_Reason,
                      err.reason,
                      "REASON_"
                    );
                    const fieldName = err.fieldHint
                      ? enumLabel(
                          CreateOrUpdateResponseUser_Field,
                          err.fieldHint,
                          "FIELD_"
                        )
                      : "";
                    return (
                      <Typography
                        key={`${rowKey}-err-${errIndex}`}
                        variant="caption"
                        component="div"
                        sx={{ wordBreak: "break-word" }}
                      >
                        <b>
                          [{levelName}] {reasonName}
                        </b>{" "}
                        {fieldName ? `(${fieldName})` : ""} — {err.message}
                      </Typography>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No errors.
                  </Typography>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const SummaryField = ({
  label,
  value,
  loading,
  loadingTestId,
}: {
  label: string;
  value: string;
  loading?: boolean;
  loadingTestId?: string;
}) => (
  <Box sx={{ minWidth: 140 }}>
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
    {loading ? (
      <Skeleton width={120} data-testid={loadingTestId} />
    ) : (
      <Typography variant="body2">{value}</Typography>
    )}
  </Box>
);

const OrgUserJobDetails = () => {
  const { regionCode, roleId, jobId } = useParams<UrlParams>();
  const { showError } = useContext(SnackbarContext);
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const canGetJob = hasPermissions([
    ActionType.ADMIN_GET_CREATE_OR_UPDATE_USERS_JOB,
  ]);

  const orgUUID = toUUID(roleId);

  const location = useLocation<{ performedByName?: string } | undefined>();
  const [performedByName, setPerformedByName] = useState<string | undefined>(
    location.state?.performedByName
  );
  const [isLoadingPerformedBy, setIsLoadingPerformedBy] = useState(false);

  const [job, setJob] = useState<CreateOrUpdateUsersJob | undefined>();
  const [users, setUsers] = useState<CreateOrUpdateResponseUser[]>([]);
  const [pageToken, setPageToken] = useState("");
  const [hasFetchedFirstPage, setHasFetchedFirstPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [activityFilter, setActivityFilter] = useState<Set<ActivityFilter>>(
    new Set()
  );
  const [resultFilter, setResultFilter] = useState<Set<ResultFilter>>(
    new Set()
  );
  const [draftActivity, setDraftActivity] = useState<Set<ActivityFilter>>(
    new Set()
  );
  const [draftResult, setDraftResult] = useState<Set<ResultFilter>>(new Set());
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);

  const fetchPage = (nextPageToken: string) => {
    setIsLoading(true);
    adminGetCreateOrUpdateUsersJob(
      {
        orgId: orgUUID,
        jobId,
        limit: USER_PAGE_LIMIT,
        nextPageToken,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then((res) => {
        setJob(res.job);
        setUsers((existing) =>
          nextPageToken === "" ? res.users : [...existing, ...res.users]
        );
        setPageToken(res.pageToken);
        setHasFetchedFirstPage(true);
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (permissions.isPermissionsLoading || !canGetJob) {
      return;
    }
    fetchPage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionCode, roleId, jobId, permissions.isPermissionsLoading, canGetJob]);

  useEffect(() => {
    if (performedByName || !job?.creatorId) {
      return;
    }
    const source =
      job.origin?.source ?? CreateOrUpdateUserOrigin_Source.UNSPECIFIED;
    if (isIntegrationSource(source)) {
      return;
    }
    const s12 = toS12ID(DocPrefix.PrefixUser, job.creatorId);
    setIsLoadingPerformedBy(true);
    fetchUserNameMap([s12], roleId, regionCode, roleId)
      .then((map) => {
        const name = map.get(s12);
        if (name) {
          setPerformedByName(name);
        }
      })
      .catch(showError)
      .finally(() => setIsLoadingPerformedBy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job]);

  const filteredUsers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users.filter((u) => {
      const username = (u.user?.username || u.user?.id || "").toLowerCase();
      if (needle && !username.includes(needle)) {
        return false;
      }
      if (!matchesActivity(u, activityFilter)) {
        return false;
      }
      if (!matchesResult(u, resultFilter)) {
        return false;
      }
      return true;
    });
  }, [users, search, activityFilter, resultFilter]);

  const openFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDraftActivity(new Set(activityFilter));
    setDraftResult(new Set(resultFilter));
    setFilterAnchor(event.currentTarget);
  };
  const closeFilter = () => setFilterAnchor(null);

  const toggleSetValue = <T,>(set: Set<T>, value: T): Set<T> => {
    const next = new Set(set);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    return next;
  };

  const handleApplyFilters = () => {
    setActivityFilter(new Set(draftActivity));
    setResultFilter(new Set(draftResult));
    closeFilter();
  };

  const handleClearDraft = () => {
    setDraftActivity(new Set());
    setDraftResult(new Set());
  };

  const activeFilterCount = activityFilter.size + resultFilter.size;

  const summary = useMemo(() => {
    if (!job) {
      return null;
    }
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
          <SummaryField
            label="Uploaded on"
            value={formatRelativeTimestamp(job.initiatedAt)}
          />
          <SummaryField
            label="Completed on"
            value={formatRelativeTimestamp(job.completedAt)}
          />
          <SummaryField
            label="Performed by"
            value={performedByName ?? getPerformedBy(job)}
            loading={isLoadingPerformedBy && !performedByName}
            loadingTestId="performed-by-loading"
          />
          <SummaryField label="Total users" value={`${job.totalUsers}`} />
          <SummaryField
            label="User upload failed"
            value={`${countByUserStatus(job, [
              CreateOrUpdateResponseUser_Status.ERRORED,
            ])}`}
          />
          <SummaryField label="Origin" value={formatOrigin(job.origin)} />
        </Stack>
      </Paper>
    );
  }, [job, performedByName, isLoadingPerformedBy]);

  if (permissions.isPermissionsLoading) {
    return (
      <CardContent>
        <CircularProgress data-testid="job-details-perm-loading" />
      </CardContent>
    );
  }

  if (!canGetJob) {
    return (
      <CardContent>
        <Typography data-testid="job-details-no-permission">
          You do not have permission to view job details
        </Typography>
      </CardContent>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            to={`/organisation/${regionCode}/${roleId}`}
            className="remove-link-style"
            style={{ color: "inherit" }}
          >
            Users
          </Link>
          <Link
            to={`/organisation/${regionCode}/${roleId}/import-history`}
            className="remove-link-style"
            style={{ color: "inherit" }}
          >
            Import history
          </Link>
          <Typography color="textPrimary" data-testid="job-details-title">
            <b>Job details</b>
          </Typography>
        </Breadcrumbs>
      </Grid>

      <Grid item xs={12}>
        {isLoading && !hasFetchedFirstPage ? (
          <LinearProgress data-testid="job-details-summary-loading" />
        ) : (
          summary
        )}
      </Grid>

      <Grid item xs={12}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search by username…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="job-details-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 320 }}
          />
          <Button
            color="primary"
            onClick={openFilter}
            data-testid="job-details-add-filter"
          >
            + Add filter
            {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Button>
          <Popover
            open={Boolean(filterAnchor)}
            anchorEl={filterAnchor}
            onClose={closeFilter}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box sx={{ p: 2, minWidth: 240 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Activity
              </Typography>
              <Stack>
                {ACTIVITY_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.key}
                    control={
                      <Checkbox
                        checked={draftActivity.has(opt.key)}
                        onChange={() =>
                          setDraftActivity((prev) =>
                            toggleSetValue(prev, opt.key)
                          )
                        }
                        data-testid={`job-details-filter-activity-${opt.key}`}
                      />
                    }
                    label={opt.label}
                  />
                ))}
              </Stack>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Result
              </Typography>
              <Stack>
                {RESULT_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.key}
                    control={
                      <Checkbox
                        checked={draftResult.has(opt.key)}
                        onChange={() =>
                          setDraftResult((prev) =>
                            toggleSetValue(prev, opt.key)
                          )
                        }
                        data-testid={`job-details-filter-result-${opt.key}`}
                      />
                    }
                    label={opt.label}
                  />
                ))}
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 2 }}
              >
                <Button size="small" onClick={handleClearDraft}>
                  Clear all
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleApplyFilters}
                  data-testid="job-details-filter-done"
                >
                  Done
                </Button>
              </Stack>
            </Box>
          </Popover>
        </Stack>
      </Grid>

      <Grid item xs={12}>
        {hasFetchedFirstPage && filteredUsers.length === 0 && !isLoading ? (
          <Alert
            severity="info"
            variant="standard"
            data-testid="job-details-empty"
          >
            {users.length === 0
              ? "No user results available for this job."
              : "No users match the current search or filters."}
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table
              aria-label="job-details-users"
              sx={{
                tableLayout: "fixed",
                minWidth: 640,
                "& thead th, & thead th .MuiTypography-root": {
                  fontWeight: 700,
                },
              }}
            >
              <colgroup>
                <col style={{ width: "38%" }} />
                <col style={{ width: "24%" }} />
                <col style={{ width: "30%" }} />
                <col style={{ width: "8%" }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Activity</TableCell>
                  <TableCell align="right">Upload results</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody data-testid="job-details-users-tbody">
                {isLoading && users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((u, index) => (
                    <UserRow
                      key={u.user?.recordId || u.user?.id || `row-${index}`}
                      user={u}
                      rowKey={u.user?.username || u.user?.id || `row-${index}`}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>

      {pageToken !== "" && !isLoading && (
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              size="small"
              onClick={() => fetchPage(pageToken)}
              data-testid="job-details-load-more"
            >
              Load more
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default OrgUserJobDetails;
